import { emitSessionExpired } from '../services/http';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';
const LOGIN_PATHS = ['/api/client-auth/login'];
const REFRESH_PATHS = ['/api/client-auth/refresh-token'];

let refreshPromise: Promise<string | null> | null = null;

const getApiUrl = (endpoint: string): string => {
  if (API_BASE_URL) {
    return `${API_BASE_URL}${endpoint}`;
  }
  return endpoint;
};

const hasAuthPath = (url: string, paths: string[]): boolean => paths.some((path) => url.includes(path));

const normalizeHeaders = (headers?: HeadersInit): Headers => {
  if (!headers) {
    return new Headers();
  }

  if (headers instanceof Headers) {
    return new Headers(headers);
  }

  if (Array.isArray(headers)) {
    return new Headers(headers);
  }

  return new Headers(Object.entries(headers));
};

const withAuthorizationHeader = (
  args: Parameters<typeof fetch>,
  token: string
): Parameters<typeof fetch> => {
  const [input, init] = args;

  if (input instanceof Request) {
    const headers = normalizeHeaders(init?.headers ?? input.headers);
    headers.set('Authorization', `Bearer ${token}`);
    return [new Request(input, { ...init, headers })];
  }

  const headers = normalizeHeaders(init?.headers);
  headers.set('Authorization', `Bearer ${token}`);
  return [input, { ...init, headers }];
};

const silentRefresh = async (originalFetch: typeof fetch): Promise<string | null> => {
  const refreshToken = localStorage.getItem('client_refresh_token');
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await originalFetch(getApiUrl('/api/client-auth/refresh-token'), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Cache-Control': 'no-store',
      },
      body: JSON.stringify({ refreshToken }),
      cache: 'no-store',
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();
    if (!data?.success || !data?.token) {
      return null;
    }

    localStorage.setItem('client_token', data.token);
    if (data.refreshToken) {
      localStorage.setItem('client_refresh_token', data.refreshToken);
    }

    return data.token;
  } catch {
    return null;
  }
};

const refreshAccessToken = async (originalFetch: typeof fetch): Promise<string | null> => {
  if (!refreshPromise) {
    refreshPromise = silentRefresh(originalFetch).finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
};

const getRuntimeApiBaseUrl = () => {
  return ((window as any).wpReactConfig?.apiUrl || import.meta.env.VITE_API_URL || '').replace(/\/$/, '');
};

const rewriteApiUrl = (url: string) => {
  if (!url.startsWith('/api/')) return url;

  const baseUrl = getRuntimeApiBaseUrl();
  return baseUrl ? `${baseUrl}${url}` : url;
};

export const setupFetchInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args: Parameters<typeof fetch>): Promise<Response> => {
    if (typeof args[0] === 'string') {
      args[0] = rewriteApiUrl(args[0]);
    }

    const url =
      typeof args[0] === 'string'
        ? args[0]
        : args[0] instanceof Request
          ? args[0].url
          : '';

    const isApiCall = url.includes('/api/');
    const isLoginRequest = hasAuthPath(url, LOGIN_PATHS);
    const isRefreshRequest = hasAuthPath(url, REFRESH_PATHS);

    const response = await originalFetch(...args);

    if (!isApiCall || isLoginRequest || isRefreshRequest || response.status !== 401) {
      return response;
    }

    const refreshedToken = await refreshAccessToken(originalFetch);
    if (refreshedToken) {
      return originalFetch(...withAuthorizationHeader(args, refreshedToken));
    }

    emitSessionExpired();
    return response;
  };
};
